import { useMemo, useState } from "react";

import DataTable from "../../../components/DataTable";
import useProjectApplications, {
  PROJECT_APPLICATIONS_FILTERS,
} from "./hooks/useProjectApplications";
import { Box, Link, Typography } from "@material-ui/core";
import { format } from "date-fns";
import GradeSelect from "./components/GradeSelect";
import CertificateButton from "./components/CertificateButton";
import useAPI from "../../../hooks/useAPI";
import PhasesModal from "./components/PhasesModal";
import cleanTitle from "../../../utils/cleanTitle";

export default function ProjectApplications() {
  const {
    data,
    isLoading,
    page,
    rowsPerPage,
    filters,
    total,
    changeFilters,
    changePage,
    changeRowsPage,
    refreshData,
    resetFilters,
  } = useProjectApplications();

  const { data: phaseStatus, isLoading: isLoadingPhaseStatus } = useAPI(
    "/admin/project-applications/phase-status"
  );
  const {
    data: applicationStatus,
    isLoading: isLoadingApplicationStatus,
  } = useAPI("/admin/project-applications/status");
  const {
    data: summary,
    isLoading: isLoadingSummary,
    refreshData: refreshSummary,
  } = useAPI("/admin/project-applications/summary");

  const [selectedProject, setSelectedProject] = useState(null);

  const resetSelectedProject = () => {
    setSelectedProject(null);
  };

  const rows = useMemo(() => {
    return data?.map((row) => {
      const projectStatus = applicationStatus?.find(
        (status) => status?.id === row?.status
      );

      return {
        step: (
          <Link
            component="button"
            underline="always"
            onClick={() => setSelectedProject(row)}
          >
            ETAPAS
          </Link>
        ),
        project: row?.id_curso ? (
          <Link
            underline="always"
            href={`/admin/cursos/conteudo?search=${row?.id_curso}`}
            target="_blank"
          >
            {cleanTitle(row?.project_name)}
          </Link>
        ) : (
          cleanTitle(row?.project_name)
        ),
        user: row?.id_pessoa ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <Link
              underline="always"
              href={`/admin/alunos/detalhes/${row?.id_pessoa}`}
              target="_blank"
            >
              {row?.user_name}
            </Link>
            <Link underline="always" href={`/admin/lms`} target="_blank">
              {row?.pessoa?.incompany_collaborator?.incompany?.name}
            </Link>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            {row?.user_name}
            <Link underline="always" href={`/admin/lms`} target="_blank">
              {row?.pessoa?.incompany_collaborator?.incompany?.name}
            </Link>
          </div>
        ),
        last_sent_at: row?.phases[0]?.updated_at
          ? format(new Date(row?.phases[0]?.updated_at), "dd/MM/yyyy hh:mm:ss")
          : "-",
        grade: (
          <div title="Projeto precisa estar aprovado para poder dar nota">
            <GradeSelect
              defaultGrade={row?.grade}
              projectApplicationID={row?.id}
              status={row?.status}
            />
          </div>
        ),
        status: (
          <Typography variant="body2" title={projectStatus?.description}>
            {projectStatus?.name}
          </Typography>
        ),
        certificate: (
          <div title="Projeto precisa estar aprovado para gerar certificado">
            <CertificateButton
              applicationID={row?.id}
              defaultCertificateUrl={row?.certificate_url}
              status={row?.status}
            />
          </div>
        ),
      };
    });
  }, [data, applicationStatus]);

  const projectNames = useMemo(() => {
    return [...new Set(summary?.map((row) => row?.project_name))];
  }, [summary]);

  const summaryRows = useMemo(() => {
    return projectNames.map((name) => {
      const projectSummary = summary?.filter(
        (row) => row?.project_name === name
      );

      const reducedProjectSummary = projectSummary?.reduce((prev, row) => {
        return {
          ...prev,
          [row?.status]: (prev[row?.status] || 0) + row?.count,
        };
      }, {});

      return {
        project: name,
        ...reducedProjectSummary,
      };
    });
  }, [summary, applicationStatus]);

  const applicationStatusFilters = Array.isArray(applicationStatus)
    ? applicationStatus.map((status) => ({ key: status.id, name: status.name }))
    : [];
  const projectNamesFilters = Array.isArray(projectNames)
    ? projectNames.map((name) => ({ key: name, name }))
    : [];

  const handleRefreshData = () => {
    refreshData();
    refreshSummary();
  };

  return (
    <Box display="flex" flexDirection="column" gridRowGap={16}>
      <DataTable
        showFilters={false}
        showPagination={false}
        rowsPerPage={6}
        maxHeight="180px"
        isLoading={isLoadingSummary && isLoadingApplicationStatus}
        columns={[
          { key: "project", name: "Projeto", isSingleLine: true },
          ...(Array.isArray(applicationStatus)
            ? applicationStatus?.map((status) => ({
                key: status?.id,
                name: status?.name,
                defaultValue: 0,
                isSingleLine: true,
              }))
            : []),
        ]}
        rows={summaryRows}
      />
      <DataTable
        isLoading={isLoading}
        showFilters
        filters={[
          {
            key: PROJECT_APPLICATIONS_FILTERS.USER,
            name: "Aluno",
            component: { type: "text" },
          },
          {
            key: PROJECT_APPLICATIONS_FILTERS.PROJECT,
            name: "Filtrar por projeto",
            component: {
              type: "select",
              options: [
                { key: "all", name: "Todos projetos" },
                ...projectNamesFilters,
              ],
            },
          },
          {
            key: PROJECT_APPLICATIONS_FILTERS.STATUS,
            name: "Filtrar por status",
            component: {
              type: "select",
              options: [
                { key: "all", name: "Todos os status" },
                ...applicationStatusFilters,
              ],
            },
          },
          {
            key: PROJECT_APPLICATIONS_FILTERS.SORT,
            name: "Ordenação",
            component: {
              type: "select",
              options: [
                { key: "desc", name: "Mais novo" },
                { key: "asc", name: "Mais antigo" },
              ],
            },
          },
        ]}
        columns={[
          { key: "step", name: "" },
          {
            key: "project",
            name: "Projeto",
            isSingleLine: true,
            maxWidth: "300px",
          },
          { key: "user", name: "Aluno", isSingleLine: true, maxWidth: "300px" },
          { key: "last_sent_at", name: "Último envio", isSingleLine: true },
          {
            key: "status",
            name: "Status",
            isSingleLine: true,
            maxWidth: "240px",
          },
          { key: "grade", name: "Nota", isSingleLine: true },
          { key: "certificate", name: "Certificado" },
        ]}
        rowsPerPage={rowsPerPage}
        rows={rows}
        page={page}
        selectedFilters={filters}
        totalRecords={total}
        onPageChange={changePage}
        onRowsPerChange={changeRowsPage}
        onSubmitFilters={changeFilters}
        onResetFilters={resetFilters}
      />
      <PhasesModal
        selectedProject={selectedProject}
        phaseStatus={phaseStatus}
        onClose={resetSelectedProject}
        onResetUserPhase={() => {
          handleRefreshData();
          setSelectedProject(null);
        }}
        onSubmitFeedback={handleRefreshData}
        onSubmitStatus={handleRefreshData}
        onResetFeedback={handleRefreshData}
      />
    </Box>
  );
}
